import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import HeatmapLayer from 'ol/layer/Heatmap';
import VectorSource from 'ol/source/Vector';
import ClusterSource from 'ol/source/Cluster';
import OSM from 'ol/source/OSM';
import TileWMS from 'ol/source/TileWMS';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Point, LineString } from 'ol/geom';
import { circular } from 'ol/geom/Polygon';
import { Feature } from 'ol';
import { Style, Text, Fill, Stroke, Circle } from 'ol/style';
import Draw from 'ol/interaction/Draw';
import GeoJSON from 'ol/format/GeoJSON';
import { getLength, getArea } from 'ol/sphere';
import { MapPin, Building, Factory, Home, AlertCircle, CheckCircle } from 'lucide-react';
import { overlayById } from '../../utils/mapOverlayLayers';
import { formatMapArea, formatMapLength } from '../../utils/mapMeasure';
import { toFeatureCollection, fitMapToSource } from '../../utils/mapGeoJson';
import 'ol/ol.css';
import './Map.css';

const InteractiveMap = ({
  enterprises = [],
  onFeatureSelect,
  onEnterpriseClick,
  onMapClick,
  onRouteStop,
  onMeasure,
  center = [-46.6333, -23.5505],
  zoom = 10,
  height = '500px',
  mapClassName = '',
  colorByLicenseStatus = false,
  radiusCenter = null,
  radiusM = 0,
  clusterEnabled = false,
  heatmapEnabled = false,
  overlayIds = [],
  clickMode = 'select',
  measureType = null,
  routeGeometry = null,
  flyTo = null,
  fitToPolygons = false,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef({});
  const drawRef = useRef(null);
  const callbacksRef = useRef({ onFeatureSelect, onMapClick, onRouteStop });
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [measureLabel, setMeasureLabel] = useState('');

  callbacksRef.current = { onFeatureSelect, onMapClick, onRouteStop };

  const getEnterpriseStyle = useCallback((enterprise) => {
    const type = enterprise?.type || 'default';

    let fillColor = '#6B7280';
    if (type === 'vistoria') {
      fillColor = '#EA580C';
    } else if (type === 'expired_license') {
      fillColor = '#B91C1C';
    } else if (type === 'complaint') {
      fillColor = '#7C3AED';
    } else if (type === 'search') {
      fillColor = '#0EA5E9';
    } else if (colorByLicenseStatus) {
      const byLic = {
        expired: '#DC2626',
        expiring: '#CA8A04',
        ok: '#16A34A',
        none: '#9CA3AF',
      };
      fillColor = byLic[enterprise.license_status] || '#9CA3AF';
    } else {
      fillColor = enterprise?.is_active ? '#10B981' : '#EF4444';
    }

    const icons = {
      industrial: '🏭',
      commercial: '🏢',
      residential: '🏠',
      agricultural: '🚜',
      default: '📍',
      complaint: '⚠️',
      vistoria: '🔎',
      expired_license: '⛔',
      search: '📍',
    };

    return new Style({
      image: new Circle({
        radius: 12,
        fill: new Fill({ color: fillColor }),
        stroke: new Stroke({ color: '#FFFFFF', width: 3 }),
      }),
      text: new Text({
        text: icons[type] || icons.default,
        font: '16px Arial',
        offsetY: -25,
        fill: new Fill({ color: '#1F2937' }),
      }),
    });
  }, [colorByLicenseStatus]);

  const createPolygonFeatures = useCallback(() => {
    const format = new GeoJSON();
    return enterprises.flatMap((enterprise) => {
      const fc = toFeatureCollection(enterprise.area_geojson);
      if (!fc) return [];
      try {
        const features = format.readFeatures(fc, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        });
        features.forEach((feature) => feature.set('enterprise', enterprise));
        return features;
      } catch {
        return [];
      }
    });
  }, [enterprises]);

  const createEnterpriseFeatures = useCallback(() => {
    return enterprises
      .map((enterprise) => {
        if (enterprise.latitude == null || enterprise.longitude == null) return null;
        if (Number.isNaN(Number(enterprise.latitude)) || Number.isNaN(Number(enterprise.longitude))) return null;
        const feature = new Feature({
          geometry: new Point(fromLonLat([enterprise.longitude, enterprise.latitude])),
          enterprise,
        });
        feature.setStyle(getEnterpriseStyle(enterprise));
        return feature;
      })
      .filter(Boolean);
  }, [enterprises, getEnterpriseStyle]);

  const clusterStyle = useCallback((feature) => {
    const members = feature.get('features') || [];
    if (members.length === 1) {
      return getEnterpriseStyle(members[0].get('enterprise'));
    }
    const size = members.length;
    return new Style({
      image: new Circle({
        radius: Math.min(28, 10 + size),
        fill: new Fill({ color: '#1D4ED8' }),
        stroke: new Stroke({ color: '#FFFFFF', width: 2 }),
      }),
      text: new Text({
        text: String(size),
        fill: new Fill({ color: '#FFFFFF' }),
        font: 'bold 12px sans-serif',
      }),
    });
  }, [getEnterpriseStyle]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const pointSource = new VectorSource();
    const clusterSource = new ClusterSource({ distance: 48, source: pointSource });
    const heatmapSource = new VectorSource();
    const radiusSource = new VectorSource();
    const routeSource = new VectorSource();
    const measureSource = new VectorSource();
    const searchSource = new VectorSource();
    const polygonSource = new VectorSource();
    const overlayGroup = [];

    const heatmapLayer = new HeatmapLayer({
      source: heatmapSource,
      blur: 18,
      radius: 10,
      weight: () => 1,
      visible: false,
    });
    const radiusLayer = new VectorLayer({
      source: radiusSource,
      style: new Style({
        fill: new Fill({ color: 'rgba(37, 99, 235, 0.12)' }),
        stroke: new Stroke({ color: '#2563EB', width: 2 }),
      }),
    });
    const routeLayer = new VectorLayer({
      source: routeSource,
      style: new Style({
        stroke: new Stroke({ color: '#1D4ED8', width: 4 }),
      }),
    });
    const measureLayer = new VectorLayer({
      source: measureSource,
      style: new Style({
        fill: new Fill({ color: 'rgba(16, 185, 129, 0.15)' }),
        stroke: new Stroke({ color: '#059669', width: 2 }),
        image: new Circle({
          radius: 5,
          fill: new Fill({ color: '#059669' }),
        }),
      }),
    });
    const searchLayer = new VectorLayer({
      source: searchSource,
      style: getEnterpriseStyle({ type: 'search', is_active: true }),
    });
    const polygonLayer = new VectorLayer({
      source: polygonSource,
      style: new Style({
        fill: new Fill({ color: 'rgba(13, 148, 136, 0.18)' }),
        stroke: new Stroke({ color: '#0F766E', width: 2 }),
      }),
    });
    const pointsLayer = new VectorLayer({
      source: clusterSource,
      style: clusterStyle,
    });

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        heatmapLayer,
        polygonLayer,
        radiusLayer,
        routeLayer,
        measureLayer,
        searchLayer,
        pointsLayer,
      ],
      view: new View({
        center: fromLonLat(center),
        zoom,
      }),
    });

    map.on('click', (event) => {
      const mode = mapInstanceRef.current?.__clickMode || 'select';
      if (mode === 'measure') return;
      const feature = map.forEachFeatureAtPixel(event.pixel, (feat) => feat);
      const members = feature?.get?.('features');
      if (members?.length > 1) {
        const extent = feature.getGeometry().getExtent();
        map.getView().fit(extent, { padding: [60, 60, 60, 60], duration: 400, maxZoom: 16 });
        return;
      }
      const enterprise = members?.[0]?.get('enterprise') || feature?.get?.('enterprise');
      if (mode === 'route') {
        const [lng, lat] = toLonLat(event.coordinate);
        callbacksRef.current.onRouteStop?.({
          lat,
          lng,
          label: enterprise?.name || 'Parada',
          id: enterprise?.id || `stop-${Date.now()}`,
        });
        return;
      }
      if (enterprise) {
        setSelectedEnterprise(enterprise);
        callbacksRef.current.onFeatureSelect?.(enterprise);
        if (enterprise.latitude != null || enterprise.lat != null) {
          callbacksRef.current.onMapClick?.(
            Number(enterprise.latitude ?? enterprise.lat),
            Number(enterprise.longitude ?? enterprise.lng)
          );
        }
      } else {
        setSelectedEnterprise(null);
        const [lng, lat] = toLonLat(event.coordinate);
        callbacksRef.current.onMapClick?.(lat, lng);
      }
    });

    map.on('pointermove', (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (feat) => feat);
      map.getTarget().style.cursor = feature ? 'pointer' : '';
    });

    layersRef.current = {
      pointSource,
      clusterSource,
      heatmapSource,
      radiusSource,
      routeSource,
      measureSource,
      searchSource,
      polygonSource,
      heatmapLayer,
      pointsLayer,
      overlayGroup,
    };
    mapInstanceRef.current = map;
    mapInstanceRef.current.__clickMode = 'select';

    const resize = () => map.updateSize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (mapRef.current && observer) observer.observe(mapRef.current);
    window.addEventListener('resize', resize);
    requestAnimationFrame(resize);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      if (drawRef.current) {
        map.removeInteraction(drawRef.current);
      }
      map.setTarget(null);
      mapInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current) mapInstanceRef.current.__clickMode = clickMode;
  }, [clickMode]);

  useEffect(() => {
    const layers = layersRef.current;
    if (!layers.pointSource) return;
    const features = createEnterpriseFeatures();
    layers.pointSource.clear();
    layers.pointSource.addFeatures(features);
    layers.heatmapSource.clear();
    layers.heatmapSource.addFeatures(features.map((f) => f.clone()));
    layers.heatmapLayer.setVisible(!!heatmapEnabled);
    layers.pointsLayer.setVisible(!heatmapEnabled);
    layers.pointsLayer.setSource(clusterEnabled ? layers.clusterSource : layers.pointSource);
    layers.pointsLayer.setStyle(clusterEnabled ? clusterStyle : (feature) => getEnterpriseStyle(feature.get('enterprise')));
    if (layers.polygonSource) {
      layers.polygonSource.clear();
      try {
        layers.polygonSource.addFeatures(createPolygonFeatures());
      } catch {
        layers.polygonSource.clear();
      }
      if (fitToPolygons && mapInstanceRef.current) {
        const map = mapInstanceRef.current;
        const fitted = fitMapToSource(map, layers.polygonSource, { padding: [40, 40, 40, 40] });
        if (!fitted) {
          requestAnimationFrame(() => {
            map.updateSize();
            fitMapToSource(map, layers.polygonSource, { padding: [40, 40, 40, 40] });
          });
        }
      }
    }
  }, [createEnterpriseFeatures, createPolygonFeatures, clusterEnabled, heatmapEnabled, clusterStyle, getEnterpriseStyle, fitToPolygons]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const layers = layersRef.current;
    if (!map || !layers.pointSource) return;
    (layers.overlayGroup || []).forEach((layer) => map.removeLayer(layer));
    const created = (overlayIds || [])
      .map((id) => overlayById(id))
      .filter(Boolean)
      .map(
        (cfg) =>
          new TileLayer({
            source: new TileWMS({
              url: cfg.url,
              params: { ...cfg.params, TRANSPARENT: true },
              crossOrigin: 'anonymous',
            }),
            opacity: 0.55,
          })
      );
    created.forEach((layer, index) => map.getLayers().insertAt(1 + index, layer));
    layers.overlayGroup = created;
  }, [overlayIds]);

  useEffect(() => {
    const src = layersRef.current.radiusSource;
    if (!src) return;
    src.clear();
    const lat = radiusCenter?.lat ?? radiusCenter?.latitude;
    const lng = radiusCenter?.lng ?? radiusCenter?.longitude;
    if (lat == null || lng == null || !radiusM) return;
    const geom = circular([Number(lng), Number(lat)], Number(radiusM), 64);
    geom.transform('EPSG:4326', 'EPSG:3857');
    src.addFeature(new Feature({ geometry: geom }));
  }, [radiusCenter, radiusM]);

  useEffect(() => {
    const src = layersRef.current.routeSource;
    if (!src) return;
    src.clear();
    if (!routeGeometry) return;
    try {
      const format = new GeoJSON();
      const features = format.readFeatures(
        { type: 'Feature', geometry: routeGeometry, properties: {} },
        { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
      );
      src.addFeatures(features);
    } catch {
      const coords = routeGeometry?.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return;
      src.addFeature(new Feature({ geometry: new LineString(coords.map((c) => fromLonLat(c))) }));
    }
  }, [routeGeometry]);

  useEffect(() => {
    const src = layersRef.current.searchSource;
    const map = mapInstanceRef.current;
    if (!src || !map) return;
    src.clear();
    if (!flyTo?.lat || !flyTo?.lon) return;
    src.addFeature(
      new Feature({
        geometry: new Point(fromLonLat([Number(flyTo.lon), Number(flyTo.lat)])),
        enterprise: { name: flyTo.label || 'Endereço', type: 'search', latitude: flyTo.lat, longitude: flyTo.lon },
      })
    );
    map.getView().animate({
      center: fromLonLat([Number(flyTo.lon), Number(flyTo.lat)]),
      zoom: 15,
      duration: 600,
    });
  }, [flyTo]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const src = layersRef.current.measureSource;
    if (!map || !src) return;
    if (drawRef.current) {
      map.removeInteraction(drawRef.current);
      drawRef.current = null;
    }
    if (clickMode !== 'measure' || !measureType) {
      return undefined;
    }
    const draw = new Draw({ source: src, type: measureType === 'area' ? 'Polygon' : 'LineString' });
    draw.on('drawend', (evt) => {
      const geom = evt.feature.getGeometry();
      const value = measureType === 'area' ? getArea(geom) : getLength(geom);
      const label = measureType === 'area' ? formatMapArea(value) : formatMapLength(value);
      setMeasureLabel(label);
      onMeasure?.({ type: measureType, value, label });
    });
    map.addInteraction(draw);
    drawRef.current = draw;
    return () => {
      map.removeInteraction(draw);
      drawRef.current = null;
    };
  }, [clickMode, measureType, onMeasure]);

  const getStatusIcon = (isActive) =>
    isActive ? <CheckCircle className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-red-500" />;

  const getTypeIcon = (type) => {
    const icons = {
      industrial: <Factory className="w-5 h-5 text-blue-500" />,
      commercial: <Building className="w-5 h-5 text-purple-500" />,
      residential: <Home className="w-5 h-5 text-green-500" />,
      agricultural: <MapPin className="w-5 h-5 text-yellow-500" />,
      default: <MapPin className="w-5 h-5 text-gray-500" />,
    };
    return icons[type] || icons.default;
  };

  return (
    <div className={`relative ${height === '100%' ? 'h-full min-h-[280px] w-full' : ''}`}>
      <div
        ref={mapRef}
        className={`w-full rounded-lg border border-gray-200 shadow-lg ${mapClassName}`.trim()}
        style={{ height, minHeight: height === '100%' ? 280 : undefined }}
      />
      {measureLabel ? (
        <div className="absolute bottom-16 right-4 z-10 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 shadow">
          {measureLabel}
        </div>
      ) : null}

      {selectedEnterprise && (
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm z-10">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              {getTypeIcon(selectedEnterprise.type)}
              <h3 className="font-semibold text-gray-900 text-lg">{selectedEnterprise.name}</h3>
            </div>
            <button type="button" onClick={() => setSelectedEnterprise(null)} className="text-gray-400 hover:text-gray-600">
              ×
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              {getStatusIcon(selectedEnterprise.is_active)}
              <span className={`text-sm font-medium ${selectedEnterprise.is_active ? 'text-green-700' : 'text-red-700'}`}>
                {selectedEnterprise.is_active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            {selectedEnterprise.description && <p className="text-sm text-gray-600">{selectedEnterprise.description}</p>}
            {selectedEnterprise.address && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Endereço:</span> {selectedEnterprise.address}
              </div>
            )}
            {selectedEnterprise.city && selectedEnterprise.state && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Localização:</span> {selectedEnterprise.city}, {selectedEnterprise.state}
              </div>
            )}
            {selectedEnterprise.latitude && selectedEnterprise.longitude && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Coordenadas:</span>{' '}
                {Number(selectedEnterprise.latitude).toFixed(6)}, {Number(selectedEnterprise.longitude).toFixed(6)}
              </div>
            )}
          </div>
          <div className="mt-4 flex space-x-2">
            <button
              type="button"
              onClick={() => onEnterpriseClick?.(selectedEnterprise)}
              className="flex-1 bg-primary-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Ver Detalhes
            </button>
            <button
              type="button"
              onClick={() => setSelectedEnterprise(null)}
              className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-10">
        <h4 className="font-semibold text-gray-900 text-sm mb-2">Legenda</h4>
        <div className="space-y-1 text-xs">
          {colorByLicenseStatus ? (
            <>
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full bg-[#16A34A]" />
                <span>Licença emitida em dia</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full bg-[#CA8A04]" />
                <span>Próximo ao vencimento (30 dias)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full bg-[#DC2626]" />
                <span>Licença vencida</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full bg-[#9CA3AF]" />
                <span>Sem licença emitida ou sem validade</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <span>Empreendimento Ativo</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <span>Empreendimento Inativo</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg border border-gray-200 p-2 z-10">
        <div className="flex flex-col space-y-1">
          <button
            type="button"
            onClick={() => {
              mapInstanceRef.current?.getView().animate({ center: fromLonLat(center), zoom, duration: 1000 });
            }}
            className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
          >
            Centralizar
          </button>
          <button
            type="button"
            onClick={() => {
              const view = mapInstanceRef.current?.getView();
              view?.animate({ zoom: view.getZoom() + 1, duration: 300 });
            }}
            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Zoom +
          </button>
          <button
            type="button"
            onClick={() => {
              const view = mapInstanceRef.current?.getView();
              view?.animate({ zoom: view.getZoom() - 1, duration: 300 });
            }}
            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Zoom -
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;
